import { useCallback, useEffect, useMemo } from "react"
import { useDispatch } from "react-redux"
import moment from "moment"
import { getToday, Period, TIME_FORMAT_TZ } from "helpers/dates"
import { DataStatus, LOADING } from "helpers/data-statuses"
import { downloadXlsx } from "helpers/csv"
import { Creators as DialogCreators } from "modules/dialog/model"
import { IsoDate } from "helpers/types/dates"
import { DownloadSheet } from "hooks/register-downloads"
import { useIsDashboardOptimizeExperimentsFeatureEnabled } from "resources/account"
import { InstrumentWithMetadata } from "helpers/types/instruments/metadata"

// [TODO:TS] Find better location
interface IntradayMetadata {
  id: string
  asOf: string
}

/**
 * Custom hook to handle intraday data refresh workflow
 * Automatically refreshes data when appropriate, or shows manual refresh button
 */
export function useIntradayRefresh(
  isIntraday: boolean,
  intradayDataAvailable: boolean,
  useLatestDate: boolean,
  lastIntradayBatch: IntradayMetadata,
  intradayBatchMetadata: IntradayMetadata,
  setIntradayBatchMetadata: (metadata: IntradayMetadata) => void,
  setPeriod: (period: Period | { period: Period, startDate: IsoDate, endDate: IsoDate }) => void,
  currentPeriod: Period,
  adjustedStartDate: IsoDate,
  // [TODO:TS] Ideally type when trades are typed, but we only care about the length
  trades: unknown[],
  resetTrades: () => void,
  onDownloadData: () => { fileName: string, data: { sheets: DownloadSheet[] } | null },
  isPreferencesLoaded: boolean,
  isEndDateIntraday: boolean,
  lastIntradayBatchStatus: DataStatus,
  setUseLatestDate: (useLatestDate: boolean) => void,
  setComparisonInstrument: (instrument: InstrumentWithMetadata | null) => void,
  setIsOptimizePanelExpanded: (isExpanded: boolean) => void,
  hasOptimizationComparison: boolean,
) {
  const dispatch = useDispatch()
  const intradayDate = getToday()
  const isOptimizeExperimentsEnabled = useIsDashboardOptimizeExperimentsFeatureEnabled()
  const newDataAvailable = isIntraday && lastIntradayBatch?.id !== intradayBatchMetadata?.id

  const shouldAutoRefresh = useMemo(() => {
    return newDataAvailable && useLatestDate && intradayDataAvailable && trades?.length === 0 && isPreferencesLoaded && !hasOptimizationComparison
  }, [newDataAvailable, useLatestDate, intradayDataAvailable, trades, isPreferencesLoaded, hasOptimizationComparison])

  const refreshIntradayData = useCallback(() => {
    setIntradayBatchMetadata({
      asOf: lastIntradayBatch?.asOf,
      id: lastIntradayBatch?.id,
    })
    setUseLatestDate(true)
    setPeriod({
      period: currentPeriod,
      startDate: adjustedStartDate,
      endDate: intradayDate,
    })
  }, [setIntradayBatchMetadata, lastIntradayBatch?.asOf, lastIntradayBatch?.id, setUseLatestDate, currentPeriod, setPeriod, adjustedStartDate, intradayDate])

  const onRefreshPositions = useCallback((hasTrades: boolean, hasOptimizationComparison: boolean) => {
    if (hasTrades) {
      resetTrades()
    }
    if (hasOptimizationComparison) {
      setComparisonInstrument(null)
      setIsOptimizePanelExpanded(false)
    }
    setIntradayBatchMetadata({
      asOf: lastIntradayBatch.asOf,
      id: lastIntradayBatch.id,
    })
    setPeriod({
      period: currentPeriod,
      startDate: adjustedStartDate,
      endDate: intradayDate,
    })
  }, [resetTrades, setComparisonInstrument, setIsOptimizePanelExpanded, setIntradayBatchMetadata, lastIntradayBatch, adjustedStartDate, intradayDate, currentPeriod, setPeriod])

  const showRefreshDialog = useCallback((
    config: {
      hasTrades: boolean
      hasOptimizationComparison: boolean
    },
    onConfirm: (hasTrades: boolean, hasOptimizationComparison: boolean) => void,
    onDownload: () => { fileName: string, data: { sheets: DownloadSheet[] } | null },
    newAsOf?: string,
  ) => {
    const { hasTrades, hasOptimizationComparison } = config
    const actions: string[] = []

    if (hasTrades) {
      actions.push("reset your trades")
    }

    if (hasOptimizationComparison) {
      actions.push("remove your optimization strategy")
    }

    const contentText = `There is new intraday data available as of ${newAsOf}. Refreshing will ${actions.join(" and ")}. Would you still like to proceed?`
    dispatch(DialogCreators.open({
      title: "Confirm Refresh and Clear",
      contentText,
      buttons: [{
        text: "Download & Refresh",
        type: "secondary",
        action: async () => {
          const { data, fileName } = onDownload()
          await downloadXlsx(fileName, data, {
            type: "intraday-refresh-download",
            location: "dashboard",
          })
          onConfirm(hasTrades, hasOptimizationComparison)
        },
      }, {
        text: "Refresh",
        type: "primary",
        action: () => onConfirm(hasTrades, hasOptimizationComparison),
      }],
    }))
  }, [dispatch])

  const handleIntradayRefresh = useCallback(() => {
    const hasTrades = trades?.length > 0

    if (hasTrades || hasOptimizationComparison) {
      const newAsOf = moment(lastIntradayBatch?.asOf, TIME_FORMAT_TZ).format("LTS")
      showRefreshDialog(
        { hasTrades, hasOptimizationComparison },
        onRefreshPositions,
        onDownloadData,
        newAsOf,
      )
    } else if (isOptimizeExperimentsEnabled) {
      setIsOptimizePanelExpanded(false)
      refreshIntradayData()
    } else {
      refreshIntradayData()
    }
  }, [trades, onRefreshPositions, onDownloadData, lastIntradayBatch, refreshIntradayData, showRefreshDialog, hasOptimizationComparison, isOptimizeExperimentsEnabled, setIsOptimizePanelExpanded])

  useEffect(() => {
    if (shouldAutoRefresh) {
      refreshIntradayData()
    }
  }, [shouldAutoRefresh, refreshIntradayData])

  // Determine if we should show the manual refresh button
  const showRefreshButton = useMemo(() => {
    // Show refresh button if:
    // 1. New data is available and won't auto-refresh, OR
    // 2. Viewing historical dates with intraday data available
    const newDataNeedsRefresh = newDataAvailable && !shouldAutoRefresh
    const historicalWithIntradayData = intradayDataAvailable && !isEndDateIntraday
    return newDataNeedsRefresh || historicalWithIntradayData
  }, [newDataAvailable, shouldAutoRefresh, intradayDataAvailable, isEndDateIntraday])

  const refreshButtonTooltip = useMemo(() => {
    const newDataNeedsRefresh = newDataAvailable && !shouldAutoRefresh
    if (newDataNeedsRefresh) {
      return "New intraday data available"
    }
    return "Load intraday data as end date"
  }, [newDataAvailable, shouldAutoRefresh])

  // Determine if we should show the warning icon
  const showWarning = useMemo(() => {
    return isIntraday && !intradayDataAvailable && lastIntradayBatchStatus !== LOADING
  }, [isIntraday, intradayDataAvailable, lastIntradayBatchStatus])

  return {
    newDataAvailable,
    showRefreshButton,
    showWarning,
    handleIntradayRefresh,
    refreshIntradayData,
    refreshButtonTooltip,
  }
}
